import api from './api.service';
import type { ApiResponse } from '@/types';
import type { CrmActivity } from './crmActivity.service';

function unwrap<T>(res: unknown): T | undefined {
  if (!res || typeof res !== 'object') return undefined;
  const r = res as ApiResponse<T>;
  if (r.data !== undefined) return r.data as T;
  return res as T;
}

export interface Opportunity {
  id: string;
  company_id: string;
  opportunity_number: string;
  lead_id?: string;
  opportunity_name: string;
  customer_id?: string;
  customer_name: string;
  contact_person?: string;
  email?: string;
  phone?: string;
  opportunity_type?: string;
  industry?: string;
  project_type?: string;
  estimated_value?: number;
  currency?: string;
  probability: number;
  expected_revenue?: number;
  expected_close_date?: string;
  project_duration_months?: number;
  stage: string;
  stage_changed_date?: string;
  status: string;
  sales_owner: string;
  sales_team_id?: string | null;
  sales_team_name?: string | null;
  sales_team?: string;
  competitors?: string;
  competitive_advantage?: string;
  win_loss_reason?: string;
  win_loss_date?: string;
  actual_close_date?: string;
  description?: string;
  requirements?: string;
  notes?: string;
  epc_context_json?: Record<string, unknown> | null;
  revenue_service_type_id?: string;
  is_converted_to_project?: boolean;
  project_id?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
  lead?: { id: string; lead_number: string; customer_name: string };
  salesOwnerUser?: { id: string; first_name: string; last_name?: string; email?: string };
  can_delete?: boolean;
  delete_blocked_reason?: string | null;
}

export interface OpportunityInput {
  opportunity_name: string;
  customer_id?: string;
  customer_name: string;
  contact_person?: string;
  email?: string;
  phone?: string;
  lead_id?: string;
  opportunity_type?: string;
  industry?: string;
  project_type?: string;
  estimated_value?: number;
  currency?: string;
  probability?: number;
  expected_close_date?: string;
  project_duration_months?: number;
  stage?: string;
  status?: string;
  sales_owner: string;
  sales_team_id?: string | null;
  sales_team_name?: string | null;
  sales_team?: string;
  competitors?: string;
  competitive_advantage?: string;
  description?: string;
  requirements?: string;
  notes?: string;
  epc_context_json?: Record<string, unknown> | null;
  revenue_service_type_id?: string | null;
}

export interface PipelineStageRow {
  stage: string;
  count: string | number;
  total_value: string | number | null;
  expected_revenue: string | number | null;
  opportunities?: {
    id: string;
    opportunity_number: string;
    opportunity_name: string;
    customer_name: string;
    estimated_value?: number;
    probability?: number;
  }[];
}

export type OpportunityActivity = CrmActivity;

export interface OpportunityRegisterFilters {
  stage?: string[];
  status?: string[];
  sales_owner?: string[];
  search?: string;
  industry?: string[];
  opportunity_type?: string[];
  project_type?: string[];
  dateFrom?: string;
  dateTo?: string;
  expectedCloseFrom?: string;
  expectedCloseTo?: string;
  minValue?: number;
  maxValue?: number;
  probabilityMin?: number;
  probabilityMax?: number;
  agingBucket?: '0-30' | '31-60' | '61-90' | '90+';
  hasNextAction?: boolean;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  sales_team_id?: string;
  missing_team?: boolean;
}

export interface OpportunityRegisterRow extends Opportunity {
  aging_days: number;
  next_action?: string | null;
  next_action_date?: string | null;
  last_activity_at?: string | null;
}

export interface OpportunityRegisterResponse {
  items: OpportunityRegisterRow[];
  total: number;
  page: number;
  pageSize: number;
  summary: {
    total_count: number;
    open_count: number;
    weighted_value: number;
    total_estimated_value: number;
    overdue_next_actions: number;
    avg_aging_days: number;
  };
}

export interface OpportunityRegisterStats {
  stage_distribution: Array<{ stage: string; count: number; weighted_value: number }>;
  aging_bands: Array<{ band: string; count: number }>;
  conversion: { won: number; lost: number; open: number };
}

export interface OpportunitySavedView {
  id: string;
  company_id: string;
  user_id: string;
  name: string;
  is_default: boolean;
  is_shared: boolean;
  filters_json?: Record<string, unknown> | null;
  columns_json?: Record<string, unknown> | null;
  sort_json?: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

class OpportunityService {
  private buildRegisterParams(filters?: OpportunityRegisterFilters): URLSearchParams {
    const params = new URLSearchParams();
    params.append('register', '1');
    if (!filters) return params;
    const appendMulti = (key: string, values?: string[]) => {
      values?.filter(Boolean).forEach((v) => params.append(key, v));
    };
    appendMulti('stage', filters.stage);
    appendMulti('status', filters.status);
    appendMulti('sales_owner', filters.sales_owner);
    appendMulti('industry', filters.industry);
    appendMulti('opportunity_type', filters.opportunity_type);
    appendMulti('project_type', filters.project_type);
    if (filters.search) params.append('search', filters.search);
    if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.append('dateTo', filters.dateTo);
    if (filters.expectedCloseFrom) params.append('expectedCloseFrom', filters.expectedCloseFrom);
    if (filters.expectedCloseTo) params.append('expectedCloseTo', filters.expectedCloseTo);
    if (filters.minValue !== undefined) params.append('minValue', String(filters.minValue));
    if (filters.maxValue !== undefined) params.append('maxValue', String(filters.maxValue));
    if (filters.probabilityMin !== undefined) params.append('probabilityMin', String(filters.probabilityMin));
    if (filters.probabilityMax !== undefined) params.append('probabilityMax', String(filters.probabilityMax));
    if (filters.agingBucket) params.append('agingBucket', filters.agingBucket);
    if (filters.hasNextAction !== undefined) params.append('hasNextAction', String(filters.hasNextAction));
    if (filters.page !== undefined) params.append('page', String(filters.page));
    if (filters.pageSize !== undefined) params.append('pageSize', String(filters.pageSize));
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
    if (filters.sales_team_id) params.append('sales_team_id', filters.sales_team_id);
    if (filters.missing_team) params.append('missing_team', 'true');
    return params;
  }

  async getAll(filters?: {
    stage?: string;
    status?: string;
    sales_owner?: string;
    search?: string;
  }): Promise<Opportunity[]> {
    const params = new URLSearchParams();
    if (filters?.stage) params.append('stage', filters.stage);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.sales_owner) params.append('sales_owner', filters.sales_owner);
    if (filters?.search) params.append('search', filters.search);
    const q = params.toString();
    const res = await api.get<Opportunity[]>(`/crm/opportunities${q ? `?${q}` : ''}`);
    const data = unwrap<Opportunity[]>(res);
    return Array.isArray(data) ? data : [];
  }

  async getPipeline(): Promise<PipelineStageRow[]> {
    const res = await api.get<PipelineStageRow[]>('/crm/opportunities/pipeline');
    const data = unwrap<PipelineStageRow[]>(res);
    return Array.isArray(data) ? data : [];
  }

  async getRegister(filters?: OpportunityRegisterFilters): Promise<OpportunityRegisterResponse> {
    const q = this.buildRegisterParams(filters).toString();
    const res = await api.get<OpportunityRegisterResponse>(`/crm/opportunities?${q}`);
    const data = unwrap<OpportunityRegisterResponse>(res);
    return (
      data || {
        items: [],
        total: 0,
        page: 1,
        pageSize: filters?.pageSize || 25,
        summary: {
          total_count: 0,
          open_count: 0,
          weighted_value: 0,
          total_estimated_value: 0,
          overdue_next_actions: 0,
          avg_aging_days: 0,
        },
      }
    );
  }

  async getRegisterStats(filters?: OpportunityRegisterFilters): Promise<OpportunityRegisterStats> {
    const q = this.buildRegisterParams(filters).toString();
    const res = await api.get<OpportunityRegisterStats>(`/crm/opportunities/register-stats?${q}`);
    const data = unwrap<OpportunityRegisterStats>(res);
    return data || { stage_distribution: [], aging_bands: [], conversion: { won: 0, lost: 0, open: 0 } };
  }

  async exportRegister(filters?: OpportunityRegisterFilters): Promise<Blob> {
    const p = this.buildRegisterParams(filters);
    p.append('format', 'csv');
    const q = p.toString();
    const response = await api.getClient().get(`/crm/opportunities/export?${q}`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  }

  async getSavedViews(): Promise<OpportunitySavedView[]> {
    const res = await api.get<OpportunitySavedView[]>('/crm/opportunities/saved-views');
    const data = unwrap<OpportunitySavedView[]>(res);
    return Array.isArray(data) ? data : [];
  }

  async createSavedView(payload: Partial<OpportunitySavedView>): Promise<OpportunitySavedView> {
    const res = await api.post<OpportunitySavedView>('/crm/opportunities/saved-views', payload);
    const data = unwrap<OpportunitySavedView>(res);
    if (!data) throw new Error('Failed to create saved view');
    return data;
  }

  async updateSavedView(id: string, payload: Partial<OpportunitySavedView>): Promise<OpportunitySavedView> {
    const res = await api.patch<OpportunitySavedView>(`/crm/opportunities/saved-views/${id}`, payload);
    const data = unwrap<OpportunitySavedView>(res);
    if (!data) throw new Error('Failed to update saved view');
    return data;
  }

  async deleteSavedView(id: string): Promise<void> {
    await api.delete(`/crm/opportunities/saved-views/${id}`);
  }

  async getById(id: string): Promise<Opportunity> {
    const res = await api.get<Opportunity>(`/crm/opportunities/${id}`);
    const data = unwrap<Opportunity>(res);
    if (!data) throw new Error('Opportunity not found');
    return data;
  }

  async create(body: OpportunityInput): Promise<Opportunity> {
    const res = await api.post<Opportunity>('/crm/opportunities', body);
    const data = unwrap<Opportunity>(res);
    if (!data) throw new Error('Failed to create opportunity');
    return data;
  }

  async update(id: string, body: Partial<OpportunityInput>): Promise<Opportunity> {
    const res = await api.put<Opportunity>(`/crm/opportunities/${id}`, body);
    const data = unwrap<Opportunity>(res);
    if (!data) throw new Error('Failed to update opportunity');
    return data;
  }

  async delete(id: string): Promise<void> {
    await api.delete(`/crm/opportunities/${id}`);
  }

  async updateStage(id: string, stage: string): Promise<Opportunity> {
    const res = await api.post<Opportunity>(`/crm/opportunities/${id}/stage`, { stage });
    const data = unwrap<Opportunity>(res);
    if (!data) throw new Error('Failed to update stage');
    return data;
  }

  async markWon(id: string, win_reason?: string): Promise<Opportunity> {
    const res = await api.post<Opportunity>(`/crm/opportunities/${id}/won`, { win_reason });
    const data = unwrap<Opportunity>(res);
    if (!data) throw new Error('Failed to mark won');
    return data;
  }

  async markLost(id: string, loss_reason: string): Promise<Opportunity> {
    const res = await api.post<Opportunity>(`/crm/opportunities/${id}/lost`, { loss_reason });
    const data = unwrap<Opportunity>(res);
    if (!data) throw new Error('Failed to mark lost');
    return data;
  }

  async convertToProject(id: string, project_id: string): Promise<Opportunity> {
    const res = await api.post<Opportunity>(`/crm/opportunities/${id}/convert-to-project`, { project_id });
    const data = unwrap<Opportunity>(res);
    if (!data) throw new Error('Failed to convert');
    return data;
  }

  async getActivities(opportunityId: string): Promise<OpportunityActivity[]> {
    const res = await api.get<OpportunityActivity[]>(`/crm/opportunities/${opportunityId}/activities`);
    const data = unwrap<OpportunityActivity[]>(res);
    return Array.isArray(data) ? data : [];
  }
}

const opportunityService = new OpportunityService();
export default opportunityService;
